import { Banner, Button } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { day } from '@vrplatform/utils';
import { Center, Group, Text } from '@mantine/core';
import { type ComponentProps, useMemo } from 'react';
import type { ListingData } from '../useListingDetailViewQuery';
import type { activeStatus_enum } from '@vrplatform/graphql';

type Props = ListingData['deactivation'] & {
  openListingDeactivationModal: () => void;
  pmsStatus: activeStatus_enum | undefined;
  sourceStatus: string | undefined;
};

export const ListingDeactivationBanner = (props: Props) => {
  const { status, openListingDeactivationModal } = props;
  const type = status === 'disabled-ending' ? 'info' : 'warning';

  const action = useMemo(
    () => getListingDeactivationAction(status, openListingDeactivationModal),
    [status, openListingDeactivationModal]
  );

  return (
    <Banner
      mounted={
        status !== 'active' ||
        (status === 'active' &&
          !!props.pmsStatus &&
          props.pmsStatus === 'inactive') ||
        props.sourceStatus === 'deleted'
      }
      type={type}
    >
      <Group wrap="nowrap" w="100%">
        <Center>
          <Icon
            icon="AlertTriangleIcon"
            size={24}
            color={(theme) =>
              theme.colors[type === 'info' ? 'blue' : 'orange'][9]
            }
          />
        </Center>

        <Text fw={500} size="md">
          {getMessage(props)}
        </Text>
        {action && (
          <Button
            leftIcon={action.icon}
            onClick={action.onClick}
            sx={{
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            {action.label}
          </Button>
        )}
      </Group>
    </Banner>
  );
};

const dayFormat = 'LL';

export const getListingDeactivationAction = (
  status: Props['status'],
  openListingDeactivationModal: () => void
): ComponentProps<typeof Banner>['action'] => {
  if (status === 'disabled' || status === 'disabled-ending') {
    return {
      label: 'Re-activate listing',
      icon: 'RefreshCwIcon',
      onClick: openListingDeactivationModal,
    };
  }

  if (status === 'upcoming') {
    return {
      label: 'Cancel deactivation',
      icon: 'CrossCircleIcon',
      onClick: openListingDeactivationModal,
    };
  }

  if (status === 'active') {
    return {
      label: 'Deactivate listing',
      icon: 'CrossCircleIcon',
      onClick: openListingDeactivationModal,
    };
  }

  return undefined;
};

const getMessage = ({ period, status, sourceStatus }: Props) => {
  if (status === 'disabled-ending') {
    return `This listing will be re-activated from ${day(period.endAt).format(dayFormat)}`;
  }

  if (status === 'disabled') {
    return `This listing was deactivated from ${day(period.startAt).format(dayFormat)}`;
  }

  if (status === 'upcoming') {
    if (period.endAt) {
      return `This listing will be deactivated from ${day(period.startAt).format(dayFormat)} until ${day(period.endAt).format(dayFormat)}`;
    }

    return `This listing will be deactivated from ${day(period.startAt).format(dayFormat)}`;
  }

  if (status === 'active') {
    if (sourceStatus === 'deleted') {
      return 'This listing was deleted in your PMS. Would you like to deactivate the listing?';
    }

    return 'We noticed this listing is deactivated in your PMS. Would you like to deactivate the listing on VRPlatform as well?';
  }

  return null;
};
